import { supabase } from './customSupabaseClient.js';

class UserIntegrationService {
  constructor() {
    this.baseUrl = supabase.supabaseUrl;
    this.apiKey = supabase.supabaseAnonKey;
  }

  // Get all unique users from bookings and reviews
  async getAllUsers() {
    try {
      // Get users from bookings
      const { data: bookingUsers, error: bookingError } = await supabase
        .from('bookings')
        .select('customer_name, customer_email, customer_phone, created_at')
        .not('customer_email', 'is', null);

      if (bookingError) {
        console.error('Error fetching booking users:', bookingError);
      }

      // Get users from reviews
      const { data: reviewUsers, error: reviewError } = await supabase
        .from('reviews')
        .select('name, email, created_at')
        .not('email', 'is', null);

      if (reviewError) {
        console.error('Error fetching review users:', reviewError);
      }

      // Combine and deduplicate users
      const allUsers = new Map();
      
      // Add booking users
      bookingUsers?.forEach(user => {
        if (user.customer_email) {
          allUsers.set(user.customer_email.toLowerCase(), {
            name: user.customer_name,
            email: user.customer_email,
            phone: user.customer_phone,
            source: 'booking',
            firstSeen: user.created_at,
            lastSeen: user.created_at
          });
        }
      });

      // Add review users (merge if exists)
      reviewUsers?.forEach(user => {
        const email = user.email.toLowerCase();
        if (allUsers.has(email)) {
          // Update existing user
          const existingUser = allUsers.get(email);
          existingUser.source = 'both';
          existingUser.lastSeen = user.created_at > existingUser.lastSeen ? user.created_at : existingUser.lastSeen;
        } else {
          // Add new user
          allUsers.set(email, {
            name: user.name,
            email: user.email,
            phone: null,
            source: 'review',
            firstSeen: user.created_at,
            lastSeen: user.created_at
          });
        }
      });

      return Array.from(allUsers.values());
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      return [];
    }
  }

  // Sync users to email marketing subscribers
  async syncUsersToEmailMarketing() {
    try {
      const users = await this.getAllUsers();
      const syncedUsers = [];
      const errors = [];

      for (const user of users) {
        try {
          // Check if user already exists in email subscribers
          const { data: existingSubscriber } = await supabase
            .from('email_subscribers')
            .select('id, email')
            .eq('email', user.email)
            .single();

          if (existingSubscriber) {
            // Update existing subscriber
            const { error: updateError } = await supabase
              .from('email_subscribers')
              .update({
                first_name: user.name.split(' ')[0] || user.name,
                last_name: user.name.split(' ').slice(1).join(' ') || '',
                phone: user.phone,
                source: user.source,
                last_activity: user.lastSeen,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingSubscriber.id);

            if (updateError) {
              errors.push({ email: user.email, error: updateError.message });
            } else {
              syncedUsers.push({ email: user.email, action: 'updated' });
            }
          } else {
            // Create new subscriber
            const { error: insertError } = await supabase
              .from('email_subscribers')
              .insert({
                email: user.email,
                first_name: user.name.split(' ')[0] || user.name,
                last_name: user.name.split(' ').slice(1).join(' ') || '',
                phone: user.phone,
                status: 'active',
                source: user.source,
                email_preferences: {
                  newsletter: true,
                  promotional: true,
                  transactional: true
                },
                tags: user.source === 'both' ? ['customer', 'reviewer'] : [user.source],
                created_at: user.firstSeen,
                last_activity: user.lastSeen
              });

            if (insertError) {
              errors.push({ email: user.email, error: insertError.message });
            } else {
              syncedUsers.push({ email: user.email, action: 'created' });
            }
          }
        } catch (userError) {
          errors.push({ email: user.email, error: userError.message });
        }
      }

      return {
        syncedUsers,
        errors,
        totalProcessed: users.length,
        successCount: syncedUsers.length,
        errorCount: errors.length
      };
    } catch (error) {
      console.error('Error in syncUsersToEmailMarketing:', error);
      return {
        syncedUsers: [],
        errors: [{ email: 'system', error: error.message }],
        totalProcessed: 0,
        successCount: 0,
        errorCount: 1
      };
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      const users = await this.getAllUsers();
      const stats = {
        totalUsers: users.length,
        bookingUsers: users.filter(u => u.source === 'booking').length,
        reviewUsers: users.filter(u => u.source === 'review').length,
        bothUsers: users.filter(u => u.source === 'both').length,
        usersWithPhone: users.filter(u => u.phone).length,
        recentUsers: users.filter(u => {
          const userDate = new Date(u.lastSeen);
          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return userDate > weekAgo;
        }).length
      };

      return stats;
    } catch (error) {
      console.error('Error in getUserStats:', error);
      return {
        totalUsers: 0,
        bookingUsers: 0,
        reviewUsers: 0,
        bothUsers: 0,
        usersWithPhone: 0,
        recentUsers: 0
      };
    }
  }

  // Get user activity timeline
  async getUserActivityTimeline() {
    try {
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('customer_name, customer_email, created_at, status, price')
        .not('customer_email', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      const { data: reviews, error: reviewError } = await supabase
        .from('reviews')
        .select('name, email, created_at, rating, is_approved')
        .not('email', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      const activities = [];

      // Add booking activities
      bookings?.forEach(booking => {
        activities.push({
          type: 'booking',
          user: booking.customer_name,
          email: booking.customer_email,
          date: booking.created_at,
          details: {
            status: booking.status,
            price: booking.price
          }
        });
      });

      // Add review activities
      reviews?.forEach(review => {
        activities.push({
          type: 'review',
          user: review.name,
          email: review.email,
          date: review.created_at,
          details: {
            rating: review.rating,
            approved: review.is_approved
          }
        });
      });

      // Sort by date
      activities.sort((a, b) => new Date(b.date) - new Date(a.date));

      return activities.slice(0, 100); // Return last 100 activities
    } catch (error) {
      console.error('Error in getUserActivityTimeline:', error);
      return [];
    }
  }

  // Check for duplicate emails in email marketing
  async checkDuplicateEmails() {
    try {
      const { data: subscribers, error: subError } = await supabase
        .from('email_subscribers')
        .select('email, first_name, last_name, source');

      if (subError) {
        console.error('Error fetching subscribers:', subError);
        return [];
      }

      // Group by email to find duplicates
      const emailGroups = {};
      subscribers?.forEach(sub => {
        const email = sub.email.toLowerCase();
        if (!emailGroups[email]) {
          emailGroups[email] = [];
        }
        emailGroups[email].push(sub);
      });

      // Find duplicates
      const duplicates = Object.entries(emailGroups)
        .filter(([email, subs]) => subs.length > 1)
        .map(([email, subs]) => ({
          email,
          count: subs.length,
          subscribers: subs
        }));

      return duplicates;
    } catch (error) {
      console.error('Error in checkDuplicateEmails:', error);
      return [];
    }
  }

  // Merge duplicate subscribers
  async mergeDuplicateSubscribers(email) {
    try {
      const { data: subscribers, error: fetchError } = await supabase
        .from('email_subscribers')
        .select('*')
        .eq('email', email);

      if (fetchError || !subscribers || subscribers.length <= 1) {
        return { success: false, message: 'No duplicates found or error fetching data' };
      }

      // Sort by created_at to keep the oldest one
      subscribers.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const primarySubscriber = subscribers[0];
      const duplicates = subscribers.slice(1);

      // Merge data from duplicates into primary
      const mergedData = { ...primarySubscriber };
      
      duplicates.forEach(dup => {
        // Merge tags
        if (dup.tags && Array.isArray(dup.tags)) {
          mergedData.tags = [...new Set([...(mergedData.tags || []), ...dup.tags])];
        }
        
        // Merge sources
        if (dup.source && mergedData.source !== dup.source) {
          mergedData.source = 'both';
        }
        
        // Keep the most recent activity
        if (dup.last_activity && new Date(dup.last_activity) > new Date(mergedData.last_activity || 0)) {
          mergedData.last_activity = dup.last_activity;
        }
      });

      // Update primary subscriber
      const { error: updateError } = await supabase
        .from('email_subscribers')
        .update(mergedData)
        .eq('id', primarySubscriber.id);

      if (updateError) {
        return { success: false, message: updateError.message };
      }

      // Delete duplicates
      const duplicateIds = duplicates.map(d => d.id);
      const { error: deleteError } = await supabase
        .from('email_subscribers')
        .delete()
        .in('id', duplicateIds);

      if (deleteError) {
        return { success: false, message: deleteError.message };
      }

      return {
        success: true,
        message: `Merged ${duplicates.length} duplicate(s) into primary subscriber`,
        primaryId: primarySubscriber.id,
        mergedCount: duplicates.length
      };
    } catch (error) {
      console.error('Error in mergeDuplicateSubscribers:', error);
      return { success: false, message: error.message };
    }
  }
}

const userIntegrationService = new UserIntegrationService();
export default userIntegrationService;
